import { utils } from '@hh/global.js'

// data-slide-driven-load: hh-slider loads the template (no poster click). Poster stays until canplay / iframe load.
// Otherwise: button poster + click to load. "loaded" hides the poster in CSS (see component-deferred-media.css).

const YT_PAUSE =
  '{"event":"command","func":"pauseVideo","args":""}'
const YT_PLAY =
  '{"event":"command","func":"playVideo","args":""}'
const YT_SEEK_ZERO =
  '{"event":"command","func":"seekTo","args":[0,true]}'
const VM_PAUSE = '{"method":"pause"}'
const VM_PLAY = '{"method":"play"}'
const VM_SEEK_ZERO = '{"method":"setCurrentTime","value":0}'

const ICON_CLASS = 'deferred-media__video-controls-icon'

const svgPlay = () =>
  `<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="${ICON_CLASS}" viewBox="0 0 20 20" fill="currentColor"><path d="M6 4.5v11l9-5.5L6 4.5z"/></svg>`
const svgPause = () =>
  `<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="${ICON_CLASS}" viewBox="0 0 20 20" fill="currentColor"><path d="M5 4h3v12H5V4zm7 0h3v12h-3V4z"/></svg>`
const svgVolOn = () =>
  `<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="${ICON_CLASS}" viewBox="0 0 24 24" fill="currentColor"><path d="M3 9v6h4l5 4V5L7 9H3zm13.5 3a4.5 4.5 0 00-2.5-4.03v8.05A4.5 4.5 0 0016.5 12zM14 3.23v2.06a7 7 0 010 13.42v2.06a9 9 0 000-17.54z"/></svg>`
const svgVolOff = () =>
  `<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="${ICON_CLASS}" viewBox="0 0 24 24" fill="currentColor"><path d="M16.5 12A4.5 4.5 0 0014 7.97v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51A8.96 8.96 0 0021 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 4v-6.73l4.25 4.25A9.05 9.05 0 0012 21v-2.06A7.04 7.04 0 0015.36 16L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/></svg>`

function pauseYoutube(iframe) {
  iframe?.contentWindow?.postMessage(YT_PAUSE, '*')
}

function playYoutube(iframe) {
  iframe?.contentWindow?.postMessage(YT_PLAY, '*')
}

function seekYoutubeStart(iframe) {
  iframe?.contentWindow?.postMessage(YT_SEEK_ZERO, '*')
}

function pauseVimeo(iframe) {
  iframe?.contentWindow?.postMessage(VM_PAUSE, '*')
}

function playVimeo(iframe) {
  iframe?.contentWindow?.postMessage(VM_PLAY, '*')
}

function seekVimeoStart(iframe) {
  iframe?.contentWindow?.postMessage(VM_SEEK_ZERO, '*')
}

class DeferredMedia extends HTMLElement {
  constructor() {
    super()
    this._onPosterClick = this.loadContent.bind(this)
    this.querySelector('button[id^="Deferred-Poster-"]')?.addEventListener(
      'click',
      this._onPosterClick
    )
  }

  connectedCallback() {
    if (!this.hasAttribute('data-video-controls')) return
    const slide = this.closest('[slot="item"]')
    if (!slide) return
    const overlay = slide.querySelector('.thumbnail__overlay-toggle')
    if (!overlay || overlay.dataset.videoControlsOverlayBound) return
    overlay.dataset.videoControlsOverlayBound = 'true'
    overlay.addEventListener('click', (e) => {
      e.preventDefault()
      e.stopPropagation()
      if (!this.hasAttribute('data-deferred-populated')) {
        this.loadContent()
      }
    })
  }

  loadContent() {
    if (this.hasAttribute('data-deferred-populated')) return

    const template = this.querySelector('template')
    if (!template?.content?.firstElementChild) return

    this.setAttribute('data-deferred-populated', '')

    const content = document.createElement('div')
    content.appendChild(template.content.firstElementChild.cloneNode(true))

    utils.pauseAllMedia()

    const media = content.querySelector('video, model-viewer, iframe')
    if (!media) return

    this.appendChild(media)

    const slideDriven = this.hasAttribute('data-slide-driven-load')

    const revealPoster = () => {
      if (this.hasAttribute('loaded')) return
      this.setAttribute('loaded', 'true')
    }

    if (media.matches('video') && slideDriven) {
      let t
      let finished = false
      const done = () => {
        if (finished) return
        finished = true
        if (t) window.clearTimeout(t)
        revealPoster()
      }
      t = window.setTimeout(done, 6500)
      media.addEventListener('canplay', done, { once: true })
      if (media.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
        done()
      }
    } else if (media.matches('iframe') && slideDriven) {
      let t
      let finished = false
      const done = () => {
        if (finished) return
        finished = true
        if (t) window.clearTimeout(t)
        revealPoster()
      }
      t = window.setTimeout(done, 8000)
      media.addEventListener('load', done, { once: true })
    } else {
      revealPoster()
    }

    try {
      media.focus({ preventScroll: true })
    } catch {
      try {
        media.focus()
      } catch {
        /* ignore */
      }
    }

    if (media.matches('video')) {
      const p = media.play?.()
      if (p && typeof p.catch === 'function') {
        p.catch(() => {})
      }
      if (this.hasAttribute('data-video-controls')) {
        this._initVideoControls(media)
      }
    }
  }

  pauseAndReset() {
    const video = this.querySelector(':scope > video')
    if (video) {
      video.pause()
      try {
        video.currentTime = 0
      } catch {
        /* ignore */
      }
      this._syncVideoControlsPlayState(video)
      return
    }
    const yt = this.querySelector(':scope > iframe.js-youtube')
    const vm = this.querySelector(':scope > iframe.js-vimeo')
    if (yt) {
      seekYoutubeStart(yt)
      pauseYoutube(yt)
    }
    if (vm) {
      seekVimeoStart(vm)
      pauseVimeo(vm)
    }
  }

  ensureLoadedAndPlay() {
    if (!this.hasAttribute('data-deferred-populated')) {
      if (this.hasAttribute('data-slide-driven-load')) {
        this.loadContent()
      }
      return
    }
    if (!this.getAttribute('loaded')) {
      this.querySelector(':scope > video')?.play?.()?.catch(() => {})
      return
    }
    const video = this.querySelector(':scope > video')
    if (video) {
      video.play?.()?.catch(() => {})
      this._syncVideoControlsPlayState(video)
      return
    }
    const yt = this.querySelector(':scope > iframe.js-youtube')
    const vm = this.querySelector(':scope > iframe.js-vimeo')
    if (yt) playYoutube(yt)
    if (vm) playVimeo(vm)
  }

  _initVideoControls(video) {
    if (this.querySelector('.deferred-media__video-controls')) return

    const wrap = document.createElement('div')
    wrap.className = 'deferred-media__video-controls'

    const playBtn = document.createElement('button')
    playBtn.type = 'button'
    playBtn.className =
      'hh-icon-control deferred-media__video-controls-btn deferred-media__video-controls-btn--play'
    playBtn.setAttribute(
      'aria-label',
      this.dataset.labelPause || 'Pause video'
    )

    const muteBtn = document.createElement('button')
    muteBtn.type = 'button'
    muteBtn.className =
      'hh-icon-control deferred-media__video-controls-btn deferred-media__video-controls-btn--mute'
    muteBtn.setAttribute('aria-pressed', video.muted ? 'true' : 'false')

    playBtn.addEventListener('click', (e) => {
      e.preventDefault()
      e.stopPropagation()
      if (video.paused) {
        video.play?.()?.catch(() => {})
      } else {
        video.pause()
      }
    })

    muteBtn.addEventListener('click', (e) => {
      e.preventDefault()
      e.stopPropagation()
      video.muted = !video.muted
      this._syncVideoControlsMuteState(video)
    })

    const onPlayPause = () => this._syncVideoControlsPlayState(video)
    video.addEventListener('play', onPlayPause)
    video.addEventListener('pause', onPlayPause)
    video.addEventListener('volumechange', () =>
      this._syncVideoControlsMuteState(video)
    )

    wrap.append(playBtn, muteBtn)
    this.appendChild(wrap)

    this._videoControlsPlayBtn = playBtn
    this._videoControlsMuteBtn = muteBtn

    this._syncVideoControlsPlayState(video)
    this._syncVideoControlsMuteState(video)
  }

  _syncVideoControlsPlayState(video) {
    const playBtn = this._videoControlsPlayBtn
    if (!playBtn || !video) return
    if (video.paused) {
      playBtn.setAttribute(
        'aria-label',
        this.dataset.labelPlay || 'Play video'
      )
      playBtn.innerHTML = svgPlay()
    } else {
      playBtn.setAttribute(
        'aria-label',
        this.dataset.labelPause || 'Pause video'
      )
      playBtn.innerHTML = svgPause()
    }
  }

  _syncVideoControlsMuteState(video) {
    const muteBtn = this._videoControlsMuteBtn
    if (!muteBtn || !video) return
    muteBtn.setAttribute('aria-pressed', video.muted ? 'true' : 'false')
    if (video.muted) {
      muteBtn.setAttribute(
        'aria-label',
        this.dataset.labelUnmute || 'Unmute video'
      )
      muteBtn.innerHTML = svgVolOff()
    } else {
      muteBtn.setAttribute(
        'aria-label',
        this.dataset.labelMute || 'Mute video'
      )
      muteBtn.innerHTML = svgVolOn()
    }
  }
}

customElements.define('deferred-media', DeferredMedia)
